import React, { type JSX } from "react";

import { Button, InformationModal } from "@impero-com/ui";

interface DeleteResourceLinkProps {
    /**
     * Text that is displayed in the link. Typically "Delete".
     */
    children: React.ReactNode;

    /**
     * Text for the OK button to confirm deletion in the confirmation dialog.
     */
    okButton: string;

    /**
     * Text for the Cancel button to cancel deletion in the confirmation dialog.
     */
    cancelButton: string;

    /**
     * Title of the confirmation dialog.
     */
    confirmationTitle: string;

    /**
     * Body of the confirmation dialog.
     */
    confirmationBody: string;

    /**
     * Can be passed from useMutation.
     */
    isPending: boolean;
    /**
     * Can be passed from useMutation.
     */
    deleteResource: () => void;
}

/**
 * A link to delete a resource that when clicked will show a confirmation dialog.
 * If user confirms deletion, the delete mutation will be called.
 *
 * The best way to use it is to wrap it into a more context specific component that
 * will inject all necessary parameters.
 */
function DeleteResourceLink({
    children,
    okButton,
    cancelButton,
    confirmationTitle,
    confirmationBody,
    isPending,
    deleteResource,
}: DeleteResourceLinkProps): JSX.Element {
    const [showConfirmation, setShowConfirmation] = React.useState(false);

    return (
        <>
            <a onClick={() => setShowConfirmation(true)}>{children}</a>
            {showConfirmation && (
                <DeleteResourceConfirmationDialog
                    onCancel={() => setShowConfirmation(false)}
                    okButton={okButton}
                    cancelButton={cancelButton}
                    confirmationTitle={confirmationTitle}
                    confirmationBody={confirmationBody}
                    isPending={isPending}
                    deleteResource={deleteResource}
                />
            )}
        </>
    );
}

interface DeleteResourceConfirmationDialogProps {
    onCancel: () => void;
    okButton: string;
    cancelButton: string;
    confirmationTitle: string;
    confirmationBody: string;
    isPending: boolean;
    deleteResource: () => void;
}

function DeleteResourceConfirmationDialog({
    onCancel,
    okButton,
    cancelButton,
    confirmationTitle,
    confirmationBody,
    isPending,
    deleteResource,
}: DeleteResourceConfirmationDialogProps): JSX.Element {
    return (
        <InformationModal
            open
            onClose={onCancel}
            type="error"
            icon="delete"
            title={confirmationTitle}
            footer={
                <>
                    <Button onClick={onCancel}>{cancelButton}</Button>
                    <Button
                        variant="destructive"
                        disabled={isPending}
                        loading={isPending}
                        onClick={() => deleteResource()}
                    >
                        {okButton}
                    </Button>
                </>
            }
        >
            {confirmationBody}
        </InformationModal>
    );
}

export { DeleteResourceLink };
